import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { ScoreGauge } from "@/components/ScoreGauge";
import { LevelBadge } from "@/components/LevelBadge";
import { calculateScore } from "@/data/questions";
import { useNavigate } from "react-router-dom";

interface ReportPreviewProps {
  level: 'low' | 'mid' | 'high';
  mockScore: number;
  mockAnswers: Record<number, string | string[]>;
}

const ReportPreview = ({ level, mockScore, mockAnswers }: ReportPreviewProps) => {
  const navigate = useNavigate();
  
  const levelConfig = {
    low: {
      title: "Raport Low (1.0-4.0)",
      color: "text-red-600",
      bgColor: "bg-red-50",
      description: "Początkowy poziom - wymaga fundamentalnych zmian"
    },
    mid: {
      title: "Raport Mid (4.1-6.9)",
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      description: "Średni poziom - potencjał do rozwoju"
    },
    high: {
      title: "Raport High (7.0-10.0)",
      color: "text-green-600",
      bgColor: "bg-green-50",
      description: "Zaawansowany poziom - doskonalenie strategii"
    }
  };

  const config = levelConfig[level];

  const handlePreview = () => {
    navigate(`/raport-${level}`, {
      state: {
        score: mockScore,
        answers: mockAnswers,
        quizSubmissionId: 'admin-preview',
        websiteScan: null
      }
    });
  };

  return (
    <Card className={`border-2 ${config.bgColor}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2 mb-2">
          <LevelBadge level={level} />
          <CardTitle className={`text-lg ${config.color}`}>{config.title}</CardTitle>
        </div>
        <p className="text-sm text-muted-foreground">{config.description}</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex justify-center py-4">
            <ScoreGauge score={mockScore} />
          </div>
          
          <div className="p-3 bg-background rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Przykładowy wynik</p>
              <p className="text-2xl font-bold">{mockScore.toFixed(1)}/10</p>
            </div>
            <Button onClick={handlePreview} variant="outline" className="w-full">
              <Eye className="mr-2 h-4 w-4" />
              Podgląd raportu
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center">
            Generuje raport z przykładowymi odpowiedziami dla poziomu {level.toUpperCase()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export const AdminReportPreview = () => {
  // Przykładowe odpowiedzi dla poziomu LOW (1.0-4.0) - NOWY SCORING
  const lowAnswers = {
    1: "no",          // Nie mam LinkedIn: 0 pkt
    2: "b2c",         // B2C: 0 pkt
    3: "only_me",     // Tylko ja: 0 pkt
    4: "example.com", // Domena (text, nie scorowane)
    6: "none",        // Nie śledzimy leadów: 0 pkt
    7: "free",        // Konto darmowe: 0 pkt
    9: "no_tools",    // Brak narzędzi: 0 pkt
    10: "under_10k",  // <10k ACV: 0.3 pkt
    11: "3months",    // Do 3 miesięcy: 0.4 pkt
    13: "no",         // Brak ICP: 0.2 pkt
    14: ["dont_know"], // Nie wiem: 0 pkt
    15: "below_2k",   // <2k wydatki: 0.4 pkt
    17: "no",         // Brak AI: 0 pkt
    18: [],           // Nic nie zna: 0 pkt
  };

  // Przykładowe odpowiedzi dla poziomu MID (4.1-6.9) - NOWY SCORING
  const midAnswers = {
    1: "inactive",    // Nieaktywny LinkedIn: 0.5 pkt
    2: "b2b",         // B2B: 1.5 pkt
    3: "2to4",        // 2-4 osoby: 0.6 pkt
    4: "example.com",
    6: "general",     // Ogólnie śledzimy: 0.8 pkt
    7: "premium",     // Premium: 0.8 pkt
    9: "crm_partial", // CRM częściowo: 0.8 pkt
    10: "10to50k",    // 10-50k ACV: 1.0 pkt
    11: "6months",    // Do 6 miesięcy: 0.6 pkt
    13: "in_progress",// ICP w trakcie: 1.2 pkt
    14: ["cold_email", "inbound"], // Cold + Inbound: 0.8 pkt
    15: "5to9k",      // 5-9k wydatki: 1.0 pkt
    17: "yes",        // AI: 1.2 pkt
    18: ["prospecting", "inbound", "lead_gen", "icp"], // 4 pojęcia: ~0.67 pkt
  };

  // Przykładowe odpowiedzi dla poziomu HIGH (7.0-10.0) - NOWY SCORING
  const highAnswers = {
    1: "yes",         // Aktywny LinkedIn: 1.2 pkt
    2: "b2b",         // B2B: 1.5 pkt
    3: "5plus",       // 5+ osób: 1.0 pkt
    4: "example.com",
    6: "detailed",    // Szczegółowa analiza: 1.5 pkt
    7: "sales_nav",   // Sales Navigator: 1.5 pkt
    9: "crm_active",  // Aktywny CRM: 1.5 pkt
    10: "over_50k",   // >50k ACV: 1.8 pkt
    11: "over_1year", // >1 rok: 1.2 pkt
    13: "yes",        // ICP gotowe: 2.5 pkt
    14: ["cold_email", "inbound", "referrals", "partners"], // 4 kanały: ~1.15 pkt
    15: "above_50k",  // >50k wydatki: 2.0 pkt
    17: "yes",        // AI: 1.2 pkt
    18: ["prospecting", "inbound", "outbound", "lead_gen", "ppc", "demand_gen", "icp", "abm", "cold_outreach", "funnel", "discovery", "personal_brand", "bant", "branding"], // Wszystkie: ~2.3 pkt
  };

  const lowScore = calculateScore(lowAnswers);
  const midScore = calculateScore(midAnswers);
  const highScore = calculateScore(highAnswers);

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Podgląd Raportów</h2>
        <p className="text-muted-foreground">
          Poniżej znajdziesz przykładowe raporty dla każdego poziomu z dynamicznie generowanym scoringiem
        </p>
      </div>

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <ReportPreview 
          level="low" 
          mockScore={lowScore} 
          mockAnswers={lowAnswers}
        />
        <ReportPreview 
          level="mid" 
          mockScore={midScore} 
          mockAnswers={midAnswers}
        />
        <ReportPreview 
          level="high" 
          mockScore={highScore} 
          mockAnswers={highAnswers}
        />
      </div>
    </div>
  );
};
